import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Pagination,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { ReactComponent as GenerateButton } from "../../images/generateButton.svg";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
    fontSize: 13,
  },
});

const ImportUserStories = ({
  almConfig,
  emptyParent,
  userStoriesLoading,
  setSelectedParent,
  userStoriesList,
  setUserStoriesList,
  selectedUserStories,
  setSelectedUserStories,
  selectedProjectName,
  setImportPageOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const totalItems = userStoriesList.length;
  const itemsPerPage = 7;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const [currentPage, setCurrentPage] = useState(1);

  const handleSelectAll = () => {
    // Select all items from unassignedStories
    setSelectedUserStories(userStoriesList);
  };

  // Deselect All: Clear the selectedItems array
  const handleDeselectAll = () => {
    setSelectedUserStories([]);
  };

  // Toggle selection of individual objects
  const handleCheckboxChange = (story) => {
    setSelectedUserStories((prevSelected) => {
      // Check if the story is already selected
      const isSelected = prevSelected.some((item) => item.id === story.id);
      if (isSelected) {
        // Remove the story from selectedItems
        return prevSelected.filter((item) => item.id !== story.id);
      } else {
        // Add the story to selectedItems
        return [...prevSelected, story];
      }
    });
  };

  // Check if a story is selected
  const isSelected = (story) => {
    return selectedUserStories.some((item) => item.id === story.id);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = userStoriesList.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleCloseUserStories = () => {
    // setSelectedParent(null);
    emptyParent();
    setUserStoriesList([]);
    setSelectedUserStories([]);
  };

  //Navigate to Flow Output page
  const handleNavigate = () => {
    if (location.pathname === "/dashboard/userstory/input") {
      navigate("/dashboard/multi-userstory/output", {
        state: { userStories: selectedUserStories, almConfig },
      });
    }
    if (location.pathname === "/dashboard/generate-testcases/input") {
      navigate("/dashboard/generate-multi/testcases/output", {
        state: { userStories: selectedUserStories, almConfig },
      });
    }
    if (location.pathname === "/dashboard/test-plan") {
      setImportPageOpen(false);
      navigate("/dashboard/test-plan", {
        state: { userStories: selectedUserStories, almConfig },
      });
    }

    if (location.pathname === "/dashboard/test-automation/robot-framework") {
      setImportPageOpen(false);
      navigate("/dashboard/test-automation/robot-framework", {
        state: { ...location.state, userStories: selectedUserStories, almConfig },
      });
    }
  };

  const handleCancel = () => {
    setImportPageOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        sx={{
          "& .MuiDialog-paper": {
            width: "600px",
            height: "600px",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ mb: -0.5, display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: -1 }}>
            <ArrowBackIosNewIcon
              sx={{
                fontSize: 11,
                color: "#000000",
                cursor: "pointer",
              }}
              onClick={handleCloseUserStories}
            />
            Import User Stories
          </Box>
          <IconButton onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <strong>{selectedProjectName}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ opacity: 0.6, mb: 1 }}>
            Select one or multiple user stories to import
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              paddingY: 1,
              ml: 1,
            }}
          >
            {/* Left: Select/Deselect Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={handleSelectAll}
                sx={{ textTransform: "none", minWidth: "50px", paddingY: 1 }}
                // startIcon={selectedUserStories.length === userStoriesList.length ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              >
                Select all
              </Button>
              <Button
                onClick={handleDeselectAll}
                sx={{ textTransform: "none", minWidth: "50px", paddingY: 1 }}
                // startIcon={selectedUserStories.length === 0 ? <CheckBoxOutlineBlankIcon /> : <IndeterminateCheckBoxIcon />}
                disabled={selectedUserStories.length === 0}
              >
                Deselect all
              </Button>
            </Box>

            {/* Right: Selected Count */}
            {/* <Typography variant="body2" >
              {selectedUserStories.length}/{userStoriesList.length} Selected
            </Typography> */}
            {/* <Typography variant="body2" sx={{ color: 'grey' }}>
              <span style={{ color: "black" }}>{selectedUserStories.length}/</span>
              <span style={{ color: "grey" }}>{userStoriesList.length}</span> Selected
            </Typography> */}
          </Box>

          <Box
            sx={{
              overflowY: "auto",
              maxHeight: "260px",
              backgroundColor: "#F0F8FF",
              border: "1px dashed #49A3FF",
              borderRadius: "7px",
              px: 3,
              py: 1,
              ml: 1,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
            }}
          >

            {userStoriesList.length > 0 ? (
              <Box sx={{ ml: 1 }}>
                {currentItems.map((item, idx) => (
                  <Box key={startIndex + idx} sx={{ mb: 0.5, ml: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected(item)}
                          onChange={() => handleCheckboxChange(item)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{item.Title}</Typography>}
                      sx={{ alignItems: "center" }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                {userStoriesLoading ? <CircularProgress /> : <Typography>No user stories found</Typography>}
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Pagination & Item Count Section */}
        <Box
          sx={{
            display: "flex",
            position: "relative",
            alignItems: "center",
            mt: 2,
            px: 2,
            justifyContent: "center",

          }}
        >
          {/* Left: Showing X/Y Items */}
          <Typography variant="body2" sx={{ flexShrink: 0, pl: 3, position: "absolute", left: 0, mb: 1 }}>
            {currentItems.length}/{userStoriesList.length} Items
          </Typography>

          {/* Center: Pagination */}
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", mb: 1 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": { color: "black" },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "lightgray",
                  color: "black",
                },
                "& .MuiPaginationItem-root:hover": { backgroundColor: "#f8f8f8" },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <DialogActions sx={{ justifyContent: "flex-end", px: 3 }}>
          <Button
            variant="contained"
            onClick={handleNavigate}
            // startIcon={<GenerateButton />}
            disabled={selectedUserStories.length <= 0}
            sx={{ backgroundColor: "#49A3FF", textTransform: "none" }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ImportUserStories;
